import axios from 'axios';

type ApiBufferEntry = {
    name: string;
    controller: AbortController;
    popup: Element | null; // Popup open when the request started (request belongs to it)
    pathname: string; // Page the request started on (route change aborts only previous pages' requests)
};

// Top-most open popup (MUI Dialog). Requests and the loader started while it is open belong to it
export const getTopOpenPopup = (): Element | null => {
    if (typeof document === "undefined") {
        return null;
    }
    const popups = document.querySelectorAll('.MuiDialog-root:not(.MuiModal-hidden)');
    return popups.length > 0 ? popups[popups.length - 1] : null;
};

const getApiBuffer = (): ApiBufferEntry[] => {
    if (typeof window === "undefined") {
        return [];
    }
    if (!Array.isArray((window as any).apiBuffer)) {
        (window as any).apiBuffer = [];
    }
    return (window as any).apiBuffer as ApiBufferEntry[];
};

export const trackApiRequest = (name: string, controller: AbortController) => {
    getApiBuffer().push({ name, controller, popup: getTopOpenPopup(), pathname: window.location.pathname });
};

export const untrackApiRequest = (name: string, controller?: AbortController) => {
    const buffer = getApiBuffer();
    const next = buffer.filter((api) => {
        if (controller) {
            return api.controller !== controller;
        }
        return api.name !== name;
    });
    buffer.length = 0;
    buffer.push(...next);
    settledListeners.forEach((listener) => listener());
};

const settledListeners = new Set<() => void>();

// Notifies whenever a tracked request finishes; returns the unsubscribe function
export const onApiRequestSettled = (listener: () => void) => {
    settledListeners.add(listener);
    return () => {
        settledListeners.delete(listener);
    };
};

export const hasInFlightApiRequests = (pathname: string) => getApiBuffer().some((api) => api.pathname === pathname);

export const isRequestAbortedError = (err: any) => {
    return Boolean(
        axios.isCancel(err)
        || err?.code === "ERR_CANCELED"
        || err?.name === "CanceledError"
        || err?.name === "AbortError"
    );
};

// Abort reason for route change: the left page's code must not resume, else its loader clear hides the new page's loader
const ROUTE_CHANGE_ABORT_REASON = "route-change";

// Aborts requests of previous pages only: the new page's layout effects (child before parent) may already have started its own requests.
// Aborted requests untrack themselves
export const abortInFlightApiRequests = () => {
    const currentPathname = window.location.pathname;
    getApiBuffer()
        .filter((api) => api.pathname !== currentPathname)
        .forEach((api) => {
            try {
                api?.controller?.abort(ROUTE_CHANGE_ABORT_REASON);
            } catch (_) {
                // no-op
            }
        });
};

// Aborts requests triggered from the given popup (e.g. user closes it); aborted requests untrack themselves
export const abortPopupApiRequests = (popup: Element | null) => {
    if (!popup) {
        return;
    }
    getApiBuffer()
        .filter((api) => api.popup === popup)
        .forEach((api) => api.controller.abort());
};

// Route change abort -> never settles, so the caller stops there (no stale setFlagLoading / handleError on the new page).
// Other aborts (e.g. popup close) -> resolve, so the caller can clear its loader
export const abortedApiResponse = (controller: AbortController) => {
    if (controller.signal.reason === ROUTE_CHANGE_ABORT_REASON) {
        return new Promise<never>(() => { });
    }
    return {
        status: false,
        flagAborted: true,
    };
};
